import { useCallback, useEffect } from 'react';
import { useDecision } from '@optimizely/react-sdk';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { EVENT_NAME, FeatureFlags, type OpalEvent } from '@frontend/constants';
import { fetchHasUnreadConversations } from '@frontend/services/messagingUnreadService';

import { useSocketChannel } from './useSocket';

const QUERY_KEY = ['messaging', 'has-unread-conversations'];

/**
 * Whether the user has unread Team Messages conversations, for the left-nav dot.
 *
 * Owned by the host rather than read off the chat component, which only mounts
 * on /chat and /messages while the nav rail is on every route. The query cache is
 * the single source of truth so nothing can latch: the socket refetches it, and
 * the chat component writes straight into it when it sees a read.
 */
export function useUnreadIm() {
  // Same decision the FeatureFlag wrapper gating the nav item uses, so this can
  // never be off for an item that is on screen. (useFeature is the legacy
  // features API and answers differently for a flag-configured key.)
  const [decision] = useDecision(FeatureFlags.MESSAGING_ENABLED);
  const messagingEnabled = decision.enabled;
  const queryClient = useQueryClient();

  const { data, refetch } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchHasUnreadConversations,
    enabled: messagingEnabled,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const handleUnreadUpdate = useCallback(() => {
    if (!messagingEnabled) {
      return;
    }
    // The event carries one conversation's count, not the inbox rollup, and it
    // also echoes back to the sender — so ask the server rather than deriving.
    void refetch();
  }, [messagingEnabled, refetch]);
  useSocketChannel(messagingEnabled ? 'im:unread:update' : null, handleUnreadUpdate);

  // The chat component knows about reads the moment they happen, well before the
  // poll would. Write its answer into the cache so it is the same value the query
  // owns, and the next fetch can still correct it.
  useEffect(() => {
    if (!messagingEnabled) {
      return;
    }
    const handleHasUnreadChanged = (
      event: OpalEvent<typeof EVENT_NAME.OPAL_IM_HAS_UNREAD_CHANGED>
    ) => {
      queryClient.setQueryData(QUERY_KEY, event.detail.hasUnreadConversations);
    };

    document.addEventListener(EVENT_NAME.OPAL_IM_HAS_UNREAD_CHANGED, handleHasUnreadChanged);

    return () => {
      document.removeEventListener(EVENT_NAME.OPAL_IM_HAS_UNREAD_CHANGED, handleHasUnreadChanged);
    };
  }, [messagingEnabled, queryClient]);

  return { hasUnreadConversations: data ?? false };
}
